import { useState, useEffect, useRef } from 'react';
import { FolderOpen, ChevronDown, ChevronRight, Play, Upload, Eye, Search, RefreshCw, Loader2, Tag, Clock, Film, Check, Copy, Save } from 'lucide-react';
import { Project } from '../types';

interface ProjectEntry {
  slug: string;
  name: string;
  status: 'in-progress' | 'done';
  created: string;
  updated: string;
  avatarUrl?: string;
  sceneCount?: number;
  duration?: number;
  versions: VersionEntry[];
}

interface VersionEntry {
  id: string;
  label: string;
  timestamp: string;
  type: 'auto' | 'manual';
  renderId?: string;
  renderUrl?: string;
}

interface LibraryPanelProps {
  onLoadVersion: (project: Project, slug: string) => void;
  currentSlug?: string | null;
  currentProject?: Project | null;
}

const S3_WORKER = 'https://scene-editor-s3.daniel-807.workers.dev';

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function LibraryPanel({ onLoadVersion, currentSlug, currentProject }: LibraryPanelProps) {
  const [projects, setProjects] = useState<ProjectEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'in-progress' | 'done'>('all');
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null);
  const [loadingVersionId, setLoadingVersionId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [savingSnapshot, setSavingSnapshot] = useState(false);
  const [snapshotLabel, setSnapshotLabel] = useState('');
  const [showSnapshotInput, setShowSnapshotInput] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState<string | null>(null);
  const snapshotInputRef = useRef<HTMLInputElement>(null);
  const [jsonModal, setJsonModal] = useState<{ slug: string; versionId: string; data: any } | null>(null);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${S3_WORKER}/list-projects`);
      const data = await res.json();
      setProjects(data.projects || []);
    } catch (err) {
      console.error('Failed to load projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${S3_WORKER}/sync-projects`);
      const data = await res.json();
      if (data.success) {
        await loadProjects();
      }
    } catch (err) {
      console.error('Sync failed:', err);
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleStatus = async (project: ProjectEntry) => {
    const newStatus = project.status === 'done' ? 'in-progress' : 'done';
    setTogglingStatus(project.slug);
    try {
      const res = await fetch(`${S3_WORKER}/update-project`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: project.slug, status: newStatus }),
      });
      const data = await res.json();
      if (data.success) {
        setProjects(projects.map(p =>
          p.slug === project.slug ? { ...p, status: newStatus } : p
        ));
      }
    } catch (err) {
      console.error('Status toggle failed:', err);
    } finally {
      setTogglingStatus(null);
    }
  };

  const handleLoadVersion = async (project: ProjectEntry, version: VersionEntry) => {
    setLoadingVersionId(version.id);
    try {
      const res = await fetch(`${S3_WORKER}/get-version?slug=${project.slug}&version=${version.id}`);
      const data = await res.json();
      if (data.storyboard) {
        onLoadVersion(data.storyboard as Project, project.slug);
      } else {
        alert('Failed to load version');
      }
    } catch (err) {
      console.error('Load version failed:', err);
      alert('Failed to load version');
    } finally {
      setLoadingVersionId(null);
    }
  };

  const handleLoadCurrent = async (project: ProjectEntry) => {
    setLoadingVersionId(`current-${project.slug}`);
    try {
      const storyboardUrl = `https://remotionlambda-useast1-2eywyfynbf.s3.us-east-1.amazonaws.com/projects/${project.slug}/storyboard.json`;
      const res = await fetch(`${S3_WORKER}/get-project?url=${encodeURIComponent(storyboardUrl)}`);
      const storyboard = await res.json();
      if (storyboard && storyboard.scenes) {
        onLoadVersion(storyboard as Project, project.slug);
      } else {
        alert('Failed to load project');
      }
    } catch (err) {
      console.error('Load current failed:', err);
      alert('Failed to load project');
    } finally {
      setLoadingVersionId(null);
    }
  };

  const handleViewJson = async (project: ProjectEntry, version: VersionEntry) => {
    try {
      const res = await fetch(`${S3_WORKER}/get-version?slug=${project.slug}&version=${version.id}`);
      const data = await res.json();
      if (data.storyboard) {
        setJsonModal({ slug: project.slug, versionId: version.id, data: data.storyboard });
      }
    } catch (err) {
      console.error('View JSON failed:', err);
    }
  };

  const handleSaveSnapshot = async () => {
    if (!currentProject || !currentSlug || !snapshotLabel.trim()) return;
    setSavingSnapshot(true);
    try {
      const res = await fetch(`${S3_WORKER}/update-project`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug: currentSlug,
          name: currentProject.name,
          avatarUrl: currentProject.source?.avatarUrl,
          sceneCount: currentProject.scenes?.length,
          duration: currentProject.scenes?.[currentProject.scenes.length - 1]?.end,
          newVersion: {
            label: snapshotLabel.trim(),
            type: 'manual',
          },
          storyboard: currentProject,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSnapshotLabel('');
        setShowSnapshotInput(false);
        await loadProjects();
      }
    } catch (err) {
      console.error('Snapshot save failed:', err);
      alert('Failed to save snapshot');
    } finally {
      setSavingSnapshot(false);
    }
  };

  const handleCopy = async (text: string, id: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredProjects = projects.filter(p => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (searchQuery && !p.name.toLowerCase().includes(searchQuery.toLowerCase()) && !p.slug.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="p-4 max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <FolderOpen className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-2xl font-bold text-gray-900">{projects.length}</p>
              <p className="text-xs text-gray-500">Projects</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {currentSlug && currentProject && (
              <>
                {showSnapshotInput ? (
                  <div className="flex items-center gap-1">
                    <input
                      ref={snapshotInputRef}
                      type="text"
                      value={snapshotLabel}
                      onChange={(e) => setSnapshotLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveSnapshot();
                        if (e.key === 'Escape') { setShowSnapshotInput(false); setSnapshotLabel(''); }
                      }}
                      placeholder="Version label..."
                      className="px-2 py-1 text-sm border border-green-500 rounded focus:outline-none focus:ring-2 focus:ring-green-500 w-40"
                      disabled={savingSnapshot}
                    />
                    <button
                      onClick={handleSaveSnapshot}
                      disabled={savingSnapshot || !snapshotLabel.trim()}
                      className="px-2 py-1 text-xs text-white bg-green-600 hover:bg-green-700 rounded disabled:opacity-50 flex items-center gap-1"
                    >
                      {savingSnapshot ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                      Save
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => { setShowSnapshotInput(true); setTimeout(() => snapshotInputRef.current?.focus(), 50); }}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-green-600 hover:bg-green-50 border border-green-200 rounded-lg transition-colors"
                  >
                    <Save className="w-3.5 h-3.5" />
                    Save Snapshot
                  </button>
                )}
              </>
            )}
            <button
              onClick={handleSync}
              disabled={syncing}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title="Scan S3 for projects"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync'}
            </button>
            <button
              onClick={loadProjects}
              disabled={loading}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search projects..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5">
            {(['all', 'in-progress', 'done'] as const).map((filter) => (
              <button
                key={filter}
                onClick={() => setStatusFilter(filter)}
                className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                  statusFilter === filter
                    ? 'bg-white shadow text-gray-900 font-medium'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {filter === 'all' ? 'All' : filter === 'in-progress' ? 'In Progress' : 'Done'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Project List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <FolderOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>{projects.length === 0 ? 'No projects found. Click "Sync" to scan S3.' : 'No matching projects'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredProjects.map((project) => (
            <div
              key={project.slug}
              className={`bg-white rounded-lg border transition-colors ${
                project.slug === currentSlug
                  ? 'border-green-300 ring-1 ring-green-200'
                  : 'border-gray-200'
              }`}
            >
              {/* Project Card Header */}
              <div
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedSlug(expandedSlug === project.slug ? null : project.slug)}
              >
                {/* Avatar Thumbnail */}
                <div className="w-10 h-14 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                  {project.avatarUrl ? (
                    <video
                      src={project.avatarUrl}
                      className="w-full h-full object-cover"
                      muted
                      preload="metadata"
                      onLoadedMetadata={(e) => { (e.target as HTMLVideoElement).currentTime = 1; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Film className="w-5 h-5 text-gray-300" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-gray-900 truncate">{project.name}</h3>
                    {project.slug === currentSlug && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full">Current</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-[11px] text-gray-400 font-mono">{project.slug}</span>
                    {project.sceneCount && (
                      <span className="text-[11px] text-gray-400">{project.sceneCount} scenes</span>
                    )}
                    {project.duration && (
                      <span className="text-[11px] text-gray-400">{formatDuration(project.duration)}</span>
                    )}
                  </div>
                </div>

                {/* Status Badge */}
                <button
                  onClick={(e) => { e.stopPropagation(); handleToggleStatus(project); }}
                  disabled={togglingStatus === project.slug}
                  className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                    project.status === 'done'
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                  }`}
                >
                  {togglingStatus === project.slug ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Tag className="w-3 h-3" />
                  )}
                  {project.status === 'done' ? 'Done' : 'In Progress'}
                </button>

                {/* Version Count & Expand */}
                <div className="flex items-center gap-2 text-gray-400">
                  <span className="text-xs">{project.versions.length} version{project.versions.length !== 1 ? 's' : ''}</span>
                  {expandedSlug === project.slug ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </div>
              </div>

              {/* Expanded: Version History + Actions */}
              {expandedSlug === project.slug && (
                <div className="border-t border-gray-100 px-4 pb-3">
                  {/* Load Current Button */}
                  <div className="py-2 border-b border-gray-100">
                    <button
                      onClick={() => handleLoadCurrent(project)}
                      disabled={loadingVersionId === `current-${project.slug}`}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {loadingVersionId === `current-${project.slug}` ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      Load Current Version
                    </button>
                  </div>

                  {/* Versions */}
                  {project.versions.length === 0 ? (
                    <p className="py-3 text-sm text-gray-400 text-center">No saved versions yet</p>
                  ) : (
                    <div className="divide-y divide-gray-50">
                      {[...project.versions].reverse().map((version) => (
                        <div key={version.id} className="flex items-center gap-3 py-2.5">
                          {/* Version Badge */}
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                            version.type === 'auto'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-purple-100 text-purple-700'
                          }`}>
                            {version.type === 'auto' ? 'Render' : 'Manual'}
                          </span>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-gray-700">{version.label}</span>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-[10px] text-gray-400">{version.id}</span>
                              <span className="text-[10px] text-gray-400">{formatDate(version.timestamp)}</span>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-0.5">
                            {/* Play render video */}
                            {version.renderUrl && (
                              <a
                                href={version.renderUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                                title="Play render"
                              >
                                <Play className="w-4 h-4" />
                              </a>
                            )}

                            {/* Copy render URL */}
                            {version.renderUrl && (
                              <button
                                onClick={() => handleCopy(version.renderUrl!, version.id + '-url')}
                                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                title="Copy render URL"
                              >
                                {copiedId === version.id + '-url' ? (
                                  <Check className="w-4 h-4 text-green-600" />
                                ) : (
                                  <Copy className="w-4 h-4" />
                                )}
                              </button>
                            )}

                            {/* View JSON */}
                            <button
                              onClick={() => handleViewJson(project, version)}
                              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                              title="View JSON"
                            >
                              <Eye className="w-4 h-4" />
                            </button>

                            {/* Load into editor */}
                            <button
                              onClick={() => handleLoadVersion(project, version)}
                              disabled={loadingVersionId === version.id}
                              className="p-1.5 text-green-500 hover:text-green-700 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                              title="Load into editor"
                            >
                              {loadingVersionId === version.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Upload className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Updated date */}
                  <div className="pt-2 flex items-center gap-1 text-[11px] text-gray-400">
                    <Clock className="w-3 h-3" />
                    Updated {formatDate(project.updated)}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* JSON Modal */}
      {jsonModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setJsonModal(null)}>
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-900">{jsonModal.slug} / {jsonModal.versionId}</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleCopy(JSON.stringify(jsonModal.data, null, 2), 'json-modal')}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
                >
                  {copiedId === 'json-modal' ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                  Copy
                </button>
                <button onClick={() => setJsonModal(null)} className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded">Close</button>
              </div>
            </div>
            <pre className="flex-1 overflow-auto p-4 text-xs font-mono text-gray-700 bg-gray-50">
              {JSON.stringify(jsonModal.data, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
